import CloseIcon from "@mui/icons-material/Close";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import Accordion from "@mui/material/Accordion";
import AccordionDetails from "@mui/material/AccordionDetails";
import AccordionSummary from "@mui/material/AccordionSummary";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import IconButton from "@mui/material/IconButton";
import { ThemeProvider } from "@mui/material/styles";
import Typography from "@mui/material/Typography";
import { useAtom } from "jotai";
import { useState } from "react";
import { useSwipeable } from "react-swipeable";
import { answeredState } from "../helpers/recoil";
import { menuColors, menuTheme } from "../theme/menuTheme";
import { ViewMode } from "../helpers/types";
import DetailCheckboxes from "./DetailCheckboxes";
import HSKLevelCheckboxes from "./HSKLevelCheckboxes";
import LengthOptions from "./LengthOptions";
import OrderButtons from "./OrderButtons";
import Score from "./Score";
import { ViewModeSection } from "./ViewModeToggle";

const sectionTitleSx = {
  fontWeight: 600,
  color: menuColors.text
};

const labelSx = {
  mb: 1,
  textAlign: "left",
  color: menuColors.textMuted
};

const DRAWER_DISMISS_DISTANCE = 72;
const DRAWER_DISMISS_VELOCITY = 0.45;

const Menu = ({
  chunkLength,
  filteredLevels,
  setFilteredLevels,
  setChunkLength,
  maxChunkLength,
  cardAmount,
  detailsOptions,
  setDetailsOptions,
  setCardAmount,
  maxCards,
  isTestMode,
  setIsTestMode,
  setShuffled,
  setShuffledCount,
  shuffledCount,
  shuffled,
  isMobile = false,
  viewMode,
  setViewMode,
  onClose
}: {
  chunkLength;
  filteredLevels;
  setFilteredLevels;
  setChunkLength;
  maxChunkLength;
  cardAmount;
  detailsOptions;
  setDetailsOptions;
  setCardAmount;
  maxCards;
  isTestMode;
  setIsTestMode;
  setShuffled;
  setShuffledCount;
  shuffledCount;
  shuffled: boolean;
  isMobile?: boolean;
  viewMode: ViewMode;
  setViewMode: (mode: ViewMode) => void;
  onClose?: () => void;
}) => {
  const handleRandomize = () => {
    setShuffled(true);
    setShuffledCount(shuffledCount + 1);
  };
  const handleReorder = () => setShuffled(false);
  const [answeredList] = useAtom(answeredState);
  const [dragOffset, setDragOffset] = useState(0);

  const drawerSwipeHandlers = useSwipeable({
    onSwiping: (eventData) => {
      setDragOffset(Math.max(0, eventData.deltaY));
    },
    onSwipedDown: (eventData) => {
      if (eventData.deltaY >= DRAWER_DISMISS_DISTANCE || eventData.velocity >= DRAWER_DISMISS_VELOCITY) {
        onClose?.();
      }
      setDragOffset(0);
    },
    onSwiped: () => {
      setDragOffset(0);
    },
    preventScrollOnSwipe: true,
    trackTouch: true,
    trackMouse: true
  });

  const menuHeader = (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        px: isMobile ? 0.5 : 0,
        pb: isMobile ? 1 : 1,
        mb: isMobile ? 0 : 1
      }}>
      <Typography variant="h6" sx={{ fontWeight: 600, color: menuColors.text }}>
        Settings
      </Typography>
      {onClose && (
        <IconButton aria-label="Close settings" onClick={onClose}>
          <CloseIcon />
        </IconButton>
      )}
    </Box>
  );

  const menuBody = (
    <>
      <ViewModeSection viewMode={viewMode} setViewMode={setViewMode} />

      {answeredList.length > 0 && (
        <Accordion defaultExpanded disableGutters elevation={0}>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Typography sx={sectionTitleSx}>Score</Typography>
          </AccordionSummary>
          <AccordionDetails>
            <Score compact={isMobile} />
          </AccordionDetails>
        </Accordion>
      )}

      <Accordion defaultExpanded={!isMobile} disableGutters elevation={0}>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Typography sx={sectionTitleSx}>HSK Level</Typography>
        </AccordionSummary>
        <AccordionDetails>
          <HSKLevelCheckboxes filteredLevels={filteredLevels} setFilteredLevels={setFilteredLevels} />
        </AccordionDetails>
      </Accordion>

      <Accordion disableGutters elevation={0}>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Typography sx={sectionTitleSx}>Deck size</Typography>
        </AccordionSummary>
        <AccordionDetails>
          <Typography variant="body2" sx={labelSx}>
            Length
          </Typography>
          <LengthOptions max={maxCards} value={cardAmount} onChange={setCardAmount} />
          <Typography variant="body2" sx={{ ...labelSx, mt: 2 }}>
            Cards per page
          </Typography>
          <LengthOptions
            max={maxChunkLength}
            value={chunkLength}
            onChange={setChunkLength}
            ariaLabel="Cards per page"
            customAriaLabel="Custom cards per page"
          />
          <Typography variant="caption" sx={{ display: "block", mt: 0.5, color: menuColors.textMuted, textAlign: "left" }}>
            Applies to study and grid. Grid paginates when cards per page is less than total cards.
          </Typography>
        </AccordionDetails>
      </Accordion>

      <Accordion disableGutters elevation={0}>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Typography sx={sectionTitleSx}>Card details</Typography>
        </AccordionSummary>
        <AccordionDetails>
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: "1fr 1fr",
              gap: 2
            }}>
            <Box>
              <Typography variant="body2" sx={{ ...sectionTitleSx, fontSize: 14 }}>
                Front
              </Typography>
              <DetailCheckboxes
                detailsOptions={detailsOptions}
                setDetailsOptions={setDetailsOptions}
                side="front"
              />
            </Box>
            <Box>
              <Typography variant="body2" sx={{ ...sectionTitleSx, fontSize: 14 }}>
                Back
              </Typography>
              <DetailCheckboxes
                detailsOptions={detailsOptions}
                setDetailsOptions={setDetailsOptions}
                side="back"
              />
            </Box>
          </Box>
        </AccordionDetails>
      </Accordion>

      <Accordion disableGutters elevation={0}>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Typography sx={sectionTitleSx}>Session</Typography>
        </AccordionSummary>
        <AccordionDetails>
          <OrderButtons shuffled={shuffled} onRandomize={handleRandomize} onReorder={handleReorder} />
          {isMobile ? null : (
            <Button
              variant="contained"
              fullWidth
              onClick={() => {
                const startingTest = !isTestMode;
                setIsTestMode(startingTest);
                if (startingTest) onClose?.();
              }}>
              {isTestMode ? "Finish test" : "Start test"}
            </Button>
          )}
        </AccordionDetails>
      </Accordion>
    </>
  );

  return (
    <ThemeProvider theme={menuTheme}>
      <Box
        className="menu"
        sx={{
          display: "flex",
          flexDirection: "column",
          maxHeight: isMobile ? "85vh" : "100vh",
          height: isMobile ? "85vh" : "auto",
          overflow: "hidden",
          color: menuColors.text,
          backgroundColor: menuColors.surface,
          transform: isMobile && dragOffset > 0 ? `translateY(${dragOffset}px)` : undefined,
          transition: isMobile && dragOffset > 0 ? "none" : "transform 0.2s ease-out",
          willChange: isMobile ? "transform" : undefined
        }}>
        {isMobile ? (
          <>
            <Box
              {...drawerSwipeHandlers}
              sx={{
                flexShrink: 0,
                px: 1.5,
                pt: "max(8px, env(safe-area-inset-top))",
                backgroundColor: menuColors.surface,
                borderBottom: `1px solid ${menuColors.border}`,
                touchAction: "none",
                cursor: "grab",
                userSelect: "none",
                "&:active": {
                  cursor: "grabbing"
                }
              }}>
              <Box
                aria-label="Drag down to close settings"
                sx={{
                  display: "flex",
                  justifyContent: "center",
                  py: 1
                }}>
                <Box
                  aria-hidden
                  sx={{
                    width: 40,
                    height: 5,
                    borderRadius: 999,
                    backgroundColor: "#64748b"
                  }}
                />
              </Box>
              {menuHeader}
            </Box>
            <Box
              sx={{
                flex: 1,
                minHeight: 0,
                overflowY: "auto",
                px: 1.5,
                pt: 1,
                pb: "max(24px, env(safe-area-inset-bottom))"
              }}>
              {menuBody}
            </Box>
          </>
        ) : (
          <Box
            sx={{
              padding: "16px",
              overflowY: "auto",
              maxHeight: "100vh"
            }}>
            {menuHeader}
            {menuBody}
          </Box>
        )}
      </Box>
    </ThemeProvider>
  );
};

export default Menu;
